import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, ElementRef, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FlaskFilter } from '../../../../../models/filter-models';
import { DisplayType, Rarity } from '../../../../../models/enums';
import { EquipmentType } from "../../../../../models/equipment-type.enum";
import { BaseComponent } from '../../../../../common/base.component';
import { EquipmentTierPickerComponent } from '../../../../../common/equipment-tier-picker/equipment-tier-picker.component';
import { Tier } from '../../../../../models/item-data-models';
import { CtrlKeyService } from '../../../../../services/ctrl-key.service';
import { NavigationService } from '../../../../../services/navigation.service';

@Component({
  selector: 'app-my-flask-charm-highlights',
  imports: [FormsModule, EquipmentTierPickerComponent],
  templateUrl: './my-flask-charm-highlights.component.html',
  styleUrl: './my-flask-charm-highlights.component.scss'
})
export class MyFlaskCharmHighlightsComponent extends BaseComponent {
  @Input({ required: true }) flask!: FlaskFilter;
  @Input() equipmentType?: string;
  @Input() equipmentIndex?: number;
  @Output() updateFilterEmitter = new EventEmitter<void>();

  EquipmentType = EquipmentType;
  Rarity = Rarity;
  DisplayType = DisplayType;
  Tier = Tier;

  toggleAllState: boolean = false;

  protected ctrlKeyService!: CtrlKeyService;
  protected navigationService!: NavigationService;
  protected elementRef!: ElementRef<HTMLElement>;
  protected hoveredElements = new Map<HTMLElement, boolean>();

  constructor() {
    super();
    this.ctrlKeyService = inject(CtrlKeyService);
    this.navigationService = inject(NavigationService);
    this.elementRef = inject(ElementRef);

    // Watch for Ctrl key state changes and update hover class accordingly
    effect(() => {
      if (!this.settings.enableRuleNavigation()) return;

      const isCtrlHeld = this.ctrlKeyService.isCtrlHeld();
      this.hoveredElements.forEach((isHovered, element) => {
        if (isHovered) {
          const titleTextElement = element.querySelector('.filter-title-text') as HTMLElement;
          if (titleTextElement) {
            if (isCtrlHeld) {
              titleTextElement.classList.add('hovered');
            } else {
              titleTextElement.classList.remove('hovered');
            }
          }
        }
      });
    });
  }

  calculateToggleAllState() {
    const booleanProperties = Object.keys(this.flask.highlightConfig).filter(key => {
      // if (this.flask.highlightConfig.equipmentType === 'flask') {
      //   if (this.poe2 && FlaskCharmHighlightConfig.poe1OnlyProperties.includes(key as keyof FlaskCharmHighlightConfig)) return false;
      //   if (this.poe1 && FlaskCharmHighlightConfig.poe2OnlyProperties.includes(key as keyof FlaskCharmHighlightConfig)) return false;
      // }
      const value = this.flask.highlightConfig[key as keyof typeof this.flask.highlightConfig];
      return typeof value === 'boolean';
    });

    const enabledCount = booleanProperties.filter(key => {
      return this.flask.highlightConfig[key as keyof typeof this.flask.highlightConfig] === true;
    }).length;

    this.toggleAllState = enabledCount > booleanProperties.length / 2;
  }

  toggleAll() {
    this.toggleAllState = !this.toggleAllState;
    // Set all boolean properties to the new toggle state
    Object.keys(this.flask.highlightConfig).forEach(key => {
      const value = this.flask.highlightConfig[key as keyof typeof this.flask.highlightConfig];
      if (typeof value === 'boolean') {
        (this.flask.highlightConfig as any)[key] = this.toggleAllState;
      }
    });
    this.updateFilter();
  }

  toggleRegularNormal() {
    if (!this.flask) return;
    this.flask.highlightConfig.regularNormal = !this.flask.highlightConfig.regularNormal;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleRegularMagic() {
    if (!this.flask) return;
    this.flask.highlightConfig.regularMagic = !this.flask.highlightConfig.regularMagic;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleILevel() {
    this.flask.highlightConfig.iLevel = !this.flask.highlightConfig.iLevel;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleQuality() {
    this.flask.highlightConfig.quality = !this.flask.highlightConfig.quality;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  getExampleGearName() {
    return this.flask.flaskBaseTypes[0] === 'All' ? this.flask.flaskClass : this.flask.flaskBaseTypes[0];
  }

  updateFilter() {
    this.updateFilterEmitter.emit();
  }


  onHighlightMouseEnter(event: MouseEvent) {
    if (!this.settings.enableRuleNavigation()) return;

    const target = event.currentTarget as HTMLElement;
    const ruleId = target.getAttribute('data-filter-rule-id');
    if (!ruleId) return;

    this.hoveredElements.set(target, true);
    if (this.ctrlKeyService.isCtrlHeld()) {
      const titleTextElement = target.querySelector('.filter-title-text') as HTMLElement;
      if (titleTextElement) {
        titleTextElement.classList.add('hovered');
      }
    }
  }

  onHighlightMouseLeave(event: MouseEvent) {
    if (!this.settings.enableRuleNavigation()) return;

    const target = event.currentTarget as HTMLElement;
    this.hoveredElements.set(target, false);
    const titleTextElement = target.querySelector('.filter-title-text') as HTMLElement;
    if (titleTextElement) {
      titleTextElement.classList.remove('hovered');
    }
  }

  onHighlightClick(event: MouseEvent) {
    if (!this.settings.enableRuleNavigation()) return;
    if (!(event.ctrlKey || event.metaKey)) return;

    const target = event.currentTarget as HTMLElement;
    const ruleId = target.getAttribute('data-filter-rule-id');
    if (!ruleId) return;

    event.preventDefault();
    event.stopPropagation();
    // Use exact match for highlight rules (they have equipment type/index appended)
    this.navigationService.navigateToFilterOutput(ruleId, false);
  }
}

